import { has } from 'lodash';
import { compose } from 'lodash/fp';
import compareAscending from 'lodash/_compareAscending';

import { attemptCast } from 'utils/bson';

/**
 * A selector generator to generate functions to get the fields to sort on.
 * Has to handle our binning and any other future postProcessing.
 *
 * This function generates an array of functions to select the values of an object in the order they need to be sorted.
 *
 * eg:
 * For quantative binning / less complicated sorting, if we have a shape like:
 * { group: { raw: 10, display: "10 - 20" } }
 *
 * We should have one selector which returns `10`.
 *
 * This also handles Decimal128 objects via the use of attemptCast.
 *
 * All selectors also handle the case of if no 'raw' value is present,
 * so similar to the above example, passing this:
 * { group: 10 }
 * will still produce [10] if passed into the selectors
 *
 * This code returns an array so we can handle if an object has no easy
 * translation to a single comparable object/primitive (numbers, strings, dates etc)
 *
 * @param {Object} channel - Channel which we need to build the sort for
 * @returns {Array<Function>}
 */
function generatePostProcessedSelectors() {
  // Handle postProcessed values, ie have a raw field
  const rawSelector = value => (has(value, 'raw') ? value.raw : value);

  // otherwise assume raw is what we want (must be a primitive)
  return [
    compose(
      attemptCast,
      rawSelector
    ),
  ];
}

/**
 * Builds a comparator for values in a given channel. Makes use of our selector generator.
 * @param {Object} channel - Channel which we need to build the comparator for
 * @returns {Function}
 */
function generatePostProcessedComparator(channel) {
  const valueSelectors = generatePostProcessedSelectors(channel);
  return function(first, second) {
    for (let idx = 0; idx < valueSelectors.length; idx++) {
      const selector = valueSelectors[idx];
      const fst = selector(first);
      const snd = selector(second);

      // special cases for undefined, we want them to be treated as small values
      // and the default lodash sorting treats them as large values
      if (fst === undefined && snd !== undefined) {
        return -1;
      } else if (fst !== undefined && snd === undefined) {
        return 1;
      }

      const result = compareAscending(fst, snd);
      if (result) {
        return result;
      }
    }
    return 0;
  };
}

export { generatePostProcessedSelectors, generatePostProcessedComparator };
